import React from 'react'
import clsx from 'clsx'
import { useDrag, useDrop } from 'react-dnd'

import DragIcon from '@SonarCX/components/icons/DragIcon'
import Edit from '../../Controls/Edit'

import useStyles from './styles'

type DraggableSettingProps = {
  obj: DraggableTaskSetting
  handleDelete: (obj: any) => void
  handleEdit: (obj: any, name?: string, external?: boolean) => void
  count?: number
  isLast: boolean
  index: number
  objType: string
  superType: string
  onDragDrop: (sourceID: string, targetID: string, after?: boolean) => void
  readOnly?: boolean
}

const DraggableSetting: React.FC<DraggableSettingProps> = ({
  obj,
  handleDelete,
  handleEdit,
  count,
  isLast,
  index,
  objType,
  superType,
  onDragDrop,
  readOnly = false
}) => {
  const DRAG_ID = React.useMemo(
    () => `${superType}_${objType}`,
    [superType, objType]
  )
  const [hovering, setHovering] = React.useState(false)
  const onDrop = React.useCallback(
    (after: boolean) => (droppedItem: any) => {
      setHovering(false)

      const sourceID = droppedItem.id
      const targetID = obj.id
      if (sourceID === targetID) {
        return
      }

      const sourceIndex: number = droppedItem.index
      const targetIndex = index
      if (sourceIndex === targetIndex) {
        return
      }

      onDragDrop(sourceID, targetID, after)
    },
    [obj.id, onDragDrop, index]
  )

  const [{ opacity }, dragRef, previewRef] = useDrag({
    // @ts-ignore
    item: { type: DRAG_ID, id: obj.id, index },
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  })

  const [{ isOver: isOverTop }, dropTop] = useDrop({
    accept: DRAG_ID,
    drop: onDrop(false),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const [{ isOver: isOverBottom }, dropBottom] = useDrop({
    accept: DRAG_ID,
    drop: onDrop(true),
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const startHovering = React.useCallback(() => setHovering(true), [])

  const endHovering = React.useCallback(() => setHovering(false), [])

  const classes = useStyles({
    isOverTop,
    isOverBottom
  })

  return (
    <div
      onMouseEnter={startHovering}
      onMouseLeave={endHovering}
      className={classes.root}
    >
      {!readOnly && (
        <div ref={dropTop} className={classes.dropContainer}>
          <div className={clsx(classes.drop, classes.dropTop)} />
        </div>
      )}
      <div ref={previewRef} className={classes.row} style={{ opacity }}>
        {hovering ? (
          <span ref={dragRef} className={classes.drag}>
            <DragIcon />
          </span>
        ) : (
          <span className={classes.emptyDrag} />
        )}

        <Edit
          obj={obj}
          objType={objType}
          superType={superType}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
          disableDelete={readOnly}
          disableEdit={readOnly}
          // @ts-ignore
          subtext={
            count > 0 ? (
              <div className={classes.bold}>
                <div>
                  Currently {count} open task
                  {count === 1 ? ' is ' : 's are '}
                  using this status
                </div>

                <div className={classes.warning}>
                  Deleting this status will automatically re-assign their status
                  to the next appropriate one.
                </div>
              </div>
            ) : (
              ''
            )
          }
          locked={obj?.system}
        />
      </div>

      {isLast && (
        <div ref={dropBottom} className={classes.dropContainer}>
          <div className={clsx(classes.drop, classes.dropBottom)} />
        </div>
      )}
    </div>
  )
}

export default DraggableSetting
